import { useTranslation } from 'react-i18next';
import { BusinessConnection, Plot } from '../../../shared/types';
import { useMemo } from 'react';
import { SEARCH_PARAMS_KEYS } from '../../../router/searchParamsKeys';
import { capitalize, inRange, max, min, uniq } from 'lodash';
import { Filter } from '../../../shared/components/NewTable/Filters/Filters';

const useSupplierTableFilters = (
  suppliers: BusinessConnection[] = []
): Filter<BusinessConnection>[] => {
  const { t } = useTranslation();
  const filterOptions = useMemo(
    () => ({
      commodities: uniq(
        suppliers?.map((supplier) => supplier.plotsCommodities).flat()
      )?.map((commodity) => ({
        label: capitalize(commodity),
        value: commodity,
      })),
      status: uniq(
        suppliers?.map((supplier) => supplier?.riskAssessment?.status ?? 'NEW')
      )?.map((status) => ({
        label: t(`suppliers.riskAssessment.status.${status}`),
        value: status,
      })),
      plotsCount: {
        min: min(suppliers?.map((supplier) => supplier.plotsCount)),
        max: (max(suppliers?.map((supplier) => supplier.plotsCount)) ?? 0) + 1,
      },
    }),
    [t, suppliers]
  );

  const filters = [
    {
      label: t('map.sidePanel.details.properties.commodities'),
      key: SEARCH_PARAMS_KEYS.FILTERS_COMMODITIES,
      match: (record: BusinessConnection, value: string) =>
        value
          ?.split(',')
          ?.some((commodity) => record?.plotsCommodities?.includes(commodity)),
      type: 'select' as const,
      options: filterOptions.commodities,
    },
    {
      label: t('plots.table.status'),
      key: SEARCH_PARAMS_KEYS.FILTERS_STATUS,
      match: (record: BusinessConnection, value: string) => {
        const status = record?.riskAssessment?.status ?? 'NEW';
        return value?.split(',')?.some((urlStatus) => status === urlStatus);
      },
      type: 'select' as const,
      options: filterOptions.status,
    },
    {
      label: t('suppliers.filters.plotsCount.label'),
      key: SEARCH_PARAMS_KEYS.FILTERS_SURFACE_AREA,
      type: 'slider' as const,
      range: filterOptions.plotsCount,
      minLabel: t('suppliers.filters.plotsCount.min'),
      maxLabel: t('suppliers.filters.plotsCount.max'),
      match: (record: BusinessConnection, value: string) => {
        const [min, max] = value?.split(',') ?? [];

        if (inRange(Number(record.plotsCount), Number(min), Number(max))) {
          return true;
        }

        return false;
      },
    },
  ];

  return filters;
};

export default useSupplierTableFilters;
